import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MapPin, Send, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "../lib/api";
import { IMG } from "../lib/images";
import { useSettings, nextMeeting } from "../lib/settings";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="card-warm overflow-hidden">
      <button
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
      >
        <span className="font-serif text-base font-semibold tracking-tight text-obsidian sm:text-lg">{q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-terracotta">
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="px-5 pb-6 text-sm leading-relaxed text-stone-600 sm:px-6 sm:text-base">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FirstVisit() {
  const { settings } = useSettings();
  const next = nextMeeting(settings);
  const [form, setForm] = useState({ first_name: "", people: 1, adults: 1, children: 0, date: format(next, "yyyy-MM-dd"), message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const FAQ = [
    { q: "Où se déroule la rencontre ?", a: `Nos rencontres ont lieu à Montévrain / Val d'Europe (Seine-et-Marne). ${settings.address}. Le bouton « Itinéraire » vous guide directement.` },
    { q: "À quelle heure dois-je arriver ?", a: `L'accueil ouvre à ${settings.welcome_time} et la rencontre commence à ${settings.meeting_time}. Arrivez quelques minutes avant si vous le souhaitez : quelqu'un sera là pour vous accueillir.` },
    { q: "Combien de temps dure la rencontre ?", a: "Environ 1h30 à 2h, suivies d'un moment convivial autour d'un café. Vous êtes libre de partir à tout moment." },
    { q: "Comment dois-je m'habiller ?", a: "Aucune tenue particulière n'est demandée. Venez simplement comme vous êtes, avec ce dans quoi vous êtes à l'aise." },
    { q: "Puis-je venir avec mes enfants ?", a: "Bien sûr ! Les enfants sont les bienvenus. La Maison de la Charité est une église familiale où chacun, petit et grand, a sa place." },
    { q: "Dois-je être chrétien pour venir ?", a: "Non. Beaucoup de personnes qui nous rejoignent découvrent la foi chrétienne pour la première fois. Vous pouvez venir avec vos questions, vos doutes, votre curiosité : vous serez accueilli sans jugement." },
    { q: "Dois-je m'inscrire ?", a: "Aucune inscription n'est nécessaire. Vous pouvez venir directement. Si vous le souhaitez, le petit formulaire ci-dessous nous permet simplement de bien vous accueillir." },
    { q: "Suis-je obligé de donner une offrande ?", a: "Absolument pas. Aucune obligation de donner de l'argent. Les dons sont libres et volontaires, et rien ne vous sera jamais demandé." },
    { q: "À quoi ressemble une rencontre ?", a: `${settings.meeting_name}, c'est un temps simple et chaleureux : louange, enseignement biblique accessible, prière, puis un moment fraternel pour échanger et faire connaissance.` },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/visit-requests", form);
      setSent(true);
      toast.success("Merci ! Nous avons bien reçu votre message.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        testid="first-visit-header"
        eyebrow="Première visite"
        titleLines={["Votre première fois à", "La Maison de la Charité ?"]}
        intro="Toutes les réponses pour venir en toute confiance. Aucune tenue particulière, aucune inscription, aucune obligation : venez simplement comme vous êtes."
        image={IMG.firstVisit}
      />

      <section className="py-16 sm:py-24">
        <div className="container-site">
          <Reveal className="mx-auto max-w-2xl">
            <div className="rounded-[2rem] bg-terracotta-soft p-7 text-center sm:p-10">
              <p className="font-serif text-2xl font-medium italic leading-snug text-terracotta-deep">
                « Vous n'êtes pas seulement invité à une réunion. Vous êtes invité à rencontrer une famille. »
              </p>
            </div>
          </Reveal>
          <div className="mx-auto mt-12 max-w-3xl space-y-3">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.04}>
                <FaqItem q={f.q} a={f.a} testid={`faq-item-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-16 sm:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="label-eyebrow">Prévenez-nous de votre venue</p>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-obsidian sm:text-4xl">
                Nous vous attendrons avec joie
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-stone-600">
                Ce petit formulaire n'est pas une inscription : il nous aide simplement à vous
                accueillir personnellement le jour J. Prochaine rencontre :
              </p>
              <div className="card-warm mt-6 p-6">
                <p className="flex items-center gap-2 font-serif text-xl font-semibold capitalize text-obsidian">
                  <CalendarDays className="h-5 w-5 text-terracotta" />
                  {format(next, "EEEE d MMMM", { locale: fr })} · {settings.meeting_time}
                </p>
                <a data-testid="visit-form-itinerary" href={settings.maps_url} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta hover:underline">
                  <MapPin className="h-4 w-4" /> Voir l'itinéraire
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="visit-form-success" className="card-warm p-10 text-center">
                <p className="font-serif text-2xl font-medium text-obsidian">Merci, à très bientôt !</p>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">
                  Votre message est bien arrivé. Nous nous réjouissons de vous accueillir —
                  venez comme vous êtes.
                </p>
                <Link to="/" className="btn-outline mt-8">Retour à l'accueil</Link>
              </div>
            ) : (
              <form data-testid="visit-form" onSubmit={submit} className="card-warm space-y-4 p-7 sm:p-9">
                <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div>
                  <label htmlFor="v-firstname" className="mb-1.5 block text-sm font-semibold text-obsidian">Prénom *</label>
                  <input id="v-firstname" data-testid="visit-firstname-input" required className="input-field" value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Votre prénom" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="v-people" className="mb-1.5 block text-sm font-semibold text-obsidian">Personnes</label>
                    <input id="v-people" data-testid="visit-people-input" type="number" min="1" max="20" className="input-field" value={form.people}
                      onChange={(e) => setForm({ ...form, people: Number(e.target.value) })} />
                  </div>
                  <div>
                    <label htmlFor="v-adults" className="mb-1.5 block text-sm font-semibold text-obsidian">Adultes</label>
                    <input id="v-adults" data-testid="visit-adults-input" type="number" min="0" max="20" className="input-field" value={form.adults}
                      onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} />
                  </div>
                  <div>
                    <label htmlFor="v-children" className="mb-1.5 block text-sm font-semibold text-obsidian">Enfants</label>
                    <input id="v-children" data-testid="visit-children-input" type="number" min="0" max="20" className="input-field" value={form.children}
                      onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} />
                  </div>
                </div>
                <div>
                  <label htmlFor="v-date" className="mb-1.5 block text-sm font-semibold text-obsidian">Date envisagée</label>
                  <input id="v-date" data-testid="visit-date-input" type="date" className="input-field" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="v-message" className="mb-1.5 block text-sm font-semibold text-obsidian">Message (facultatif)</label>
                  <textarea id="v-message" data-testid="visit-message-input" rows={4} className="input-field resize-none" value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Une question, une précision ? Écrivez-nous librement." />
                </div>
                <button data-testid="visit-submit-btn" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  <Send className="h-4 w-4" />
                  {loading ? "Envoi…" : "Prévenir de ma venue"}
                </button>
                <p className="text-center text-xs text-stone-400">Vos informations restent entre nous — voir notre politique de confidentialité.</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
